import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import { getProjectBySlug } from "@/data/projects";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);
  const [openIdx, setOpenIdx] = useState(null);
  const [openDrawing, setOpenDrawing] = useState(null);

  if (!project) return <Navigate to="/portfolio" replace />;

  const cover = project.scenes[project.coverScene || 0];

  return (
    <div data-testid="page-project-detail">
      {/* HERO */}
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={cover.src} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/80 to-[#0A0A0A]" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Link to="/portfolio" data-testid="back-to-portfolio" className="overline inline-flex items-center gap-2 mb-8 text-[#A3A3A3] hover:text-[#B58B5D] transition-colors">
            <ArrowLeft size={14} /> Back to portfolio
          </Link>
          <div className="grid md:grid-cols-12 gap-8">
            <div className="md:col-span-9">
              <div className="overline mb-6">— {project.type} · {project.year}</div>
              <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] leading-[0.95] tracking-tighter text-[#EAEAEA]">
                {project.title}
              </h1>
              <p className="mt-6 text-xl text-[#A3A3A3] max-w-2xl">{project.location}</p>
            </div>
          </div>
        </div>
      </section>

      {/* COVER IMAGE */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16" data-testid="project-cover">
        <Reveal>
          <button
            onClick={() => setOpenIdx(project.coverScene || 0)}
            className="block w-full overflow-hidden border border-white/5 group"
            data-testid="cover-open"
          >
            <img src={cover.src} alt={cover.alt} className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]" />
          </button>
        </Reveal>
      </section>

      {/* SUMMARY + META */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-12 gap-12" data-testid="project-summary">
        <Reveal className="md:col-span-7">
          <div className="overline mb-4">— Project</div>
          <p className="font-serif text-3xl md:text-4xl leading-tight text-[#EAEAEA] mb-8">
            {project.summary}
          </p>
          <p className="text-[#A3A3A3] text-lg leading-relaxed">{project.description}</p>
        </Reveal>
        <Reveal className="md:col-span-4 md:col-start-9 space-y-8" delay={120}>
          <div>
            <div className="overline mb-2">Type</div>
            <p className="text-[#EAEAEA]">{project.type}</p>
          </div>
          <div>
            <div className="overline mb-2">Year</div>
            <p className="text-[#EAEAEA]">{project.year}</p>
          </div>
          <div>
            <div className="overline mb-2">Status</div>
            <p className="text-[#EAEAEA]">{project.status}</p>
          </div>
          {project.software?.length > 0 && (
            <div>
              <div className="overline mb-2">Software</div>
              <p className="text-[#EAEAEA]">{project.software.join(" · ")}</p>
            </div>
          )}
          {project.discipline?.length > 0 && (
            <div>
              <div className="overline mb-2">Discipline</div>
              <ul className="space-y-1">
                {project.discipline.map((d) => (
                  <li key={d} className="text-[#EAEAEA] text-sm">— {d}</li>
                ))}
              </ul>
            </div>
          )}
        </Reveal>
      </section>

      {/* DELIVERABLES */}
      {project.deliverables?.length > 0 && (
        <section className="bg-[#0E0E0E] border-y border-white/5 py-20" data-testid="project-deliverables">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-4">
              <div className="overline mb-3">— Deliverables</div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">What we produced.</h2>
            </Reveal>
            <ul className="md:col-span-7 md:col-start-6 grid sm:grid-cols-2 gap-x-8">
              {project.deliverables.map((d, i) => (
                <Reveal key={d} delay={i * 40}>
                  <li className="py-4 border-b border-white/5 flex gap-4 text-[15px]">
                    <span className="num text-[#B58B5D]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[#EAEAEA]">{d}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* RENDERS GALLERY */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24" data-testid="project-renders">
        <Reveal>
          <div className="overline mb-3">— Renders</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-12">All scenes &mdash; <em className="text-[#B58B5D] not-italic">{project.scenes.length}</em></h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {project.scenes.map((s, i) => (
            <Reveal key={s.src} delay={i * 50}>
              <button
                data-testid={`render-${i}`}
                onClick={() => setOpenIdx(i)}
                className="group block w-full overflow-hidden border border-white/5 text-left"
              >
                <div className="overflow-hidden">
                  <img src={s.thumb} alt={s.alt} className="w-full h-auto aspect-[3/2] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="flex items-center justify-between p-4 bg-[#0E0E0E] border-t border-white/5">
                  <span className="font-mono uppercase text-[11px] tracking-[0.2em] text-[#A3A3A3]">Scene {String(i + 1).padStart(2, "0")}</span>
                  <span className="font-mono uppercase text-[11px] tracking-[0.2em] text-[#B58B5D] flex items-center gap-1">View <ArrowUpRight size={12} /></span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SHOP DRAWINGS (if any) */}
      {project.shopDrawings?.length > 0 && (
        <section className="bg-[#0E0E0E] border-y border-white/5 py-24" data-testid="project-shop-drawings">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal>
              <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
                <div>
                  <div className="overline mb-3">— Shop drawings</div>
                  <h2 className="font-serif text-4xl md:text-5xl leading-tight">Production documentation.</h2>
                  <p className="text-[#A3A3A3] mt-3 max-w-xl">{project.shopDrawings.length} sheet{project.shopDrawings.length === 1 ? "" : "s"} — plan, elevations, sections and details.</p>
                </div>
                {project.shopDrawingsPdf && (
                  <a
                    href={project.shopDrawingsPdf}
                    target="_blank"
                    rel="noreferrer"
                    download
                    data-testid="download-drawings-pdf"
                    className="dl-btn-primary"
                  >
                    Download PDF <ArrowUpRight size={14} />
                  </a>
                )}
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.shopDrawings.map((d, i) => (
                <Reveal key={d.src} delay={i * 50}>
                  <button
                    onClick={() => setOpenDrawing(i)}
                    data-testid={`drawing-${i}`}
                    className="w-full text-left dl-card group overflow-hidden"
                  >
                    <div className="aspect-[3/2] overflow-hidden bg-white">
                      <img src={d.thumb || d.src} alt={d.title} loading="lazy" className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.02]" />
                    </div>
                    <div className="flex items-center justify-between p-5 border-t border-white/5">
                      <div>
                        <div className="overline mb-1">{d.kind || "Drawing"}</div>
                        <p className="text-[#EAEAEA] group-hover:text-[#B58B5D] transition-colors">{d.title}</p>
                      </div>
                      <span className="font-mono uppercase text-[11px] tracking-[0.2em] text-[#B58B5D] flex items-center gap-1">View <ArrowUpRight size={12} /></span>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NEXT STEP */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center" data-testid="project-cta">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Working on something similar?
          </h2>
          <p className="text-[#A3A3A3] max-w-xl mx-auto mb-8">Send your plans, references or current model. We will scope visualization, cabinetry design or full production documentation around your requirements.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="dl-btn-primary">Discuss a project</Link>
            <Link to="/portfolio" className="dl-btn">More work</Link>
          </div>
        </Reveal>
      </section>

      <Lightbox scenes={project.scenes} openIndex={openIdx} onClose={() => setOpenIdx(null)} />
      {project.shopDrawings?.length > 0 && (
        <Lightbox
          scenes={project.shopDrawings.map((d) => ({ src: d.src, alt: d.title }))}
          openIndex={openDrawing}
          onClose={() => setOpenDrawing(null)}
        />
      )}
    </div>
  );
}
